import { selectIsAiBusinessToolsEnabled } from 'app-components/flux/ai-chat/aiChatSelectors';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { getLogger } from '../../../utils/core';
import { allTools as rawTools } from './handlers';
import { businessTools } from './handlers/business';
import { wrapHandler } from './llmToolsFramework';
import type { ChartTransformer, ToolDefinition, ToolEntry, ToolHandler } from './llmToolsTypes';

const log = getLogger('llmToolsCatalog');

type AllToolsType = Record<string, ToolEntry>;

const businessToolNames = new Set(Object.keys(businessTools));

// Auto-wrap all handlers with error handling and logging
const allTools: AllToolsType = Object.entries(rawTools).reduce((acc, [name, tool]) => {
  acc[name] = {
    definition: tool.definition,
    handler: wrapHandler(name, tool.handler),
    chartTransformer: 'chartTransformer' in tool ? (tool.chartTransformer as ChartTransformer) : undefined,
  };
  return acc;
}, {} as AllToolsType);

// Public lookup so the message handler can resolve by name
export function getToolHandler(name: string): ToolHandler | undefined {
  const state = store?.getState();
  const platformSpecificToolsHandlers = state?.aiChatStore?.platformSpecificToolsHandlers || {};

  // First check platform-specific handlers, then check our tools registry
  const handler = platformSpecificToolsHandlers[name] || allTools[name]?.handler;

  log.debug('getToolHandler', { name, found: !!handler });
  return handler;
}

export function getToolDefinitions(): ToolDefinition[] {
  const state = store?.getState() as RootState | undefined;
  const areBusinessToolsEnabled = state ? selectIsAiBusinessToolsEnabled(state) : false;
  return Object.values(allTools).reduce<ToolDefinition[]>((acc, tool) => {
    if (areBusinessToolsEnabled || !businessToolNames.has(tool.definition.name)) acc.push(tool.definition);
    return acc;
  }, []);
}

export async function buildClientTools(): Promise<ToolDefinition[]> {
  return getToolDefinitions();
}

export function getToolNames(): string[] {
  return Object.keys(allTools);
}

export function getChartTransformer(name: string): ChartTransformer | undefined {
  return allTools[name]?.chartTransformer;
}
